import React, { useState } from "react";
import { MapPin, Edit2, Star, Trash, Plus, Home, Building2, MapPinned } from "lucide-react";
import type { AddressManagerProps } from "./types";
import ConfirmModal from "../_ResponsiveComponents/Modal/ConfirmModal";

const AddressManager: React.FC<AddressManagerProps> = ({
  addresses,
  addressTypes,
  selectedAddress,
  selectedAddressType,
  hasAddresses,
  addressesLoading,
  onSelectAddress,
  onSelectAddressType,
  onShowAddressModal,
  onShowAddAddressModal,
  onDeleteAddress,
  onSetDefaultAddress,
  onEditAddress,
}) => {
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  if (addressesLoading) {
    return (
      <div className="bg-section-card-bg rounded-2xl shadow-sm border border-section-border p-6 mb-6">
        <h2 className="text-2xl font-bold mb-4 flex items-center gap-3 text-section-text">
          <MapPin className="w-6 h-6 text-primary" />
          Shipping Address
        </h2>
        <div className="flex items-center justify-center p-12">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
        </div>
      </div>
    );
  }

  if (!hasAddresses) {
    return (
      <div className="bg-section-card-bg rounded-2xl shadow-sm border border-section-border overflow-hidden mb-6">
        <div className="p-6 border-b border-section-border">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-section-text">
            <MapPin className="w-6 h-6 text-primary" />
            Shipping Address
          </h2>
        </div>
        <div className="p-8">
          <div className="border-2 border-dashed border-section-border bg-section-surface rounded-2xl p-8 text-center">
            <div className="relative inline-block mb-4">
              <div className="absolute inset-0 bg-gold/10 rounded-full blur-xl opacity-50"></div>
              <MapPin className="relative w-16 h-16 text-section-muted mx-auto" />
            </div>
            <h3 className="text-xl font-bold text-section-text mb-2">No addresses found</h3>
            <p className="text-section-muted mb-6 max-w-sm mx-auto">
              Add your first shipping address to continue with checkout
            </p>
            <button
              onClick={onShowAddAddressModal}
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-xl font-bold hover:opacity-90 hover:-translate-y-0.5 transition-all shadow-[0_4px_14px_rgba(201,169,110,0.2)]"
            >
              <Plus className="w-5 h-5" />
              Add Address
            </button>
          </div>
        </div>
      </div>
    );
  }

  const getAddressTypeIcon = (typeName: string) => {
    const lowerName = typeName.toLowerCase();
    if (lowerName.includes("home")) return <Home className="w-4 h-4" />;
    if (lowerName.includes("work") || lowerName.includes("office")) return <Building2 className="w-4 h-4" />;
    return <MapPinned className="w-4 h-4" />;
  };

  return (
    <div className="bg-section-card-bg rounded-2xl shadow-sm border border-section-border overflow-hidden mb-6">
      {/* Header */}
      <div className="p-6 border-b border-section-border">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold flex items-center gap-3 text-section-text">
            <MapPin className="w-6 h-6 text-primary" />
            Shipping Address
          </h2>
          <button
            onClick={onShowAddAddressModal}
            className="flex items-center gap-2 bg-section-surface hover:bg-section-border text-primary px-4 py-2 rounded-xl font-semibold border border-section-border hover:border-primary/30 transition-all shadow-sm"
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Add New</span>
          </button>
        </div>
      </div>

      <div className="p-6">
        {/* Address Type Tabs */}
        <div className="flex items-center gap-2 mb-6 border-b border-section-border overflow-x-auto pb-1" style={{ scrollbarWidth: "thin" }}>
          {addressTypes.map((type) => (
            <button
              key={type.id}
              onClick={() => {
                onSelectAddressType(type.id);
                const addressForType = addresses.find(
                  (addr) => addr.address_type_id === type.id || addr.address_type_id?.toString() === type.id.toString(),
                );
                if (addressForType) onSelectAddress(addressForType);
              }}
              className={`relative px-5 py-3 font-semibold transition-all duration-300 rounded-t-xl whitespace-nowrap ${
                selectedAddressType === type.id || selectedAddressType?.toString() === type.id.toString()
                  ? "text-primary bg-primary/5 border-b-4 border-primary"
                  : "text-section-muted hover:text-primary hover:bg-section-surface"
              }`}
            >
              <span className="flex items-center gap-2">
                {getAddressTypeIcon(type.name)}
                {type.name}
              </span>
            </button>
          ))}
        </div>

        {/* Selected Address Display */}
        {selectedAddress && selectedAddressType && (
          <div className="relative group">
            <div className="absolute -inset-1 bg-gold/10 rounded-2xl blur opacity-25 group-hover:opacity-40 transition duration-300"></div>
            <div
              className="relative border border-gold/30 rounded-2xl p-6 cursor-pointer hover:border-gold/60 transition-all duration-200 bg-section-card-bg shadow-sm hover:shadow-md"
              onClick={onShowAddressModal}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-primary rounded-xl flex items-center justify-center shadow-sm">
                      <MapPin className="w-6 h-6 text-primary-foreground" />
                    </div>
                    <div>
                      <h3 className="font-bold text-section-text text-lg">
                        {selectedAddress.address_type_value || "Address"}
                      </h3>
                      {selectedAddress.is_default && (
                        <div className="flex items-center gap-1 text-xs text-amber-600 bg-amber-50 border border-amber-200 px-2 py-1 rounded-full mt-1">
                          <Star className="w-3 h-3 fill-current" />
                          <span className="font-semibold">Default Address</span>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 text-section-text ml-15">
                    <div className="font-semibold text-base">{selectedAddress.street}</div>
                    {[
                      selectedAddress.building && `Building ${selectedAddress.building}`,
                      selectedAddress.floor && `Floor ${selectedAddress.floor}`,
                      selectedAddress.apartment && `Apt ${selectedAddress.apartment}`,
                    ].filter(Boolean).length > 0 && (
                      <div className="text-sm text-section-muted">
                        {[
                          selectedAddress.building && `Building ${selectedAddress.building}`,
                          selectedAddress.floor && `Floor ${selectedAddress.floor}`,
                          selectedAddress.apartment && `Apt ${selectedAddress.apartment}`,
                        ].filter(Boolean).join(" • ")}
                      </div>
                    )}
                    <div className="text-sm font-medium text-section-text">{selectedAddress.city}</div>
                    {selectedAddress.description && (
                      <div className="text-sm text-section-muted italic mt-2 pt-2 border-t border-section-border">
                        {selectedAddress.description}
                      </div>
                    )}
                  </div>
                </div>

                {/* Desktop actions */}
                <div className="ml-4 flex items-center gap-2">
                  <div className="hidden md:flex flex-col gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (onEditAddress && selectedAddress) onEditAddress(selectedAddress);
                        else onShowAddressModal();
                      }}
                      className="p-2.5 rounded-xl bg-section-surface border border-section-border text-primary hover:bg-section-border hover:border-primary/30 transition-all duration-200 group/btn shadow-sm"
                      title="Edit address"
                    >
                      <Edit2 className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); setConfirmDeleteOpen(true); }}
                      className="p-2.5 rounded-xl bg-section-surface border border-section-border text-red-500 hover:bg-red-50 hover:border-red-200 transition-all duration-200 group/btn shadow-sm"
                      title="Delete address"
                    >
                      <Trash className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                    </button>
                    {!selectedAddress.is_default && (
                      <button
                        onClick={(e) => { e.stopPropagation(); onSetDefaultAddress(selectedAddress.id); }}
                        className="p-2.5 rounded-xl bg-section-surface border border-section-border text-amber-500 hover:bg-amber-50 hover:border-amber-200 transition-all duration-200 group/btn shadow-sm"
                        title="Set as default"
                      >
                        <Star className="w-4 h-4 group-hover/btn:scale-110 transition-transform" />
                      </button>
                    )}
                  </div>

                  {/* Mobile: 3-dots dropdown */}
                  <details className="relative md:hidden" onClick={(e) => e.stopPropagation()}>
                    <summary
                      className="p-3 rounded-xl bg-section-surface border border-section-border text-primary hover:bg-section-border hover:border-primary/30 transition-all duration-200 cursor-pointer flex items-center justify-center shadow-sm"
                      aria-label="More options"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                        <circle cx="12" cy="5" r="1.5" />
                        <circle cx="12" cy="12" r="1.5" />
                        <circle cx="12" cy="19" r="1.5" />
                      </svg>
                    </summary>

                    <div className="absolute right-0 mt-2 w-48 bg-section-card-bg border border-section-border rounded-xl shadow-xl z-50 overflow-hidden">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onEditAddress && selectedAddress) onEditAddress(selectedAddress);
                          else onShowAddressModal();
                          const d = e.currentTarget.closest("details") as HTMLDetailsElement | null;
                          if (d) d.open = false;
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-section-surface flex items-center gap-3 transition-colors"
                      >
                        <Edit2 className="w-4 h-4 text-primary" />
                        <span className="text-sm font-medium text-section-text">Edit</span>
                      </button>

                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirmDeleteOpen(true);
                          const d = e.currentTarget.closest("details") as HTMLDetailsElement | null;
                          if (d) d.open = false;
                        }}
                        className="w-full text-left px-4 py-3 hover:bg-red-50 flex items-center gap-3 transition-colors"
                      >
                        <Trash className="w-4 h-4 text-red-500" />
                        <span className="text-sm font-medium text-red-500">Delete</span>
                      </button>

                      {!selectedAddress.is_default && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onSetDefaultAddress(selectedAddress.id);
                            const d = e.currentTarget.closest("details") as HTMLDetailsElement | null;
                            if (d) d.open = false;
                          }}
                          className="w-full text-left px-4 py-3 hover:bg-amber-50 flex items-center gap-3 transition-colors"
                        >
                          <Star className="w-4 h-4 text-amber-500" />
                          <span className="text-sm font-medium text-section-text">Set as default</span>
                        </button>
                      )}
                    </div>
                  </details>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* No address for selected type */}
        {selectedAddressType &&
          !addresses.find(
            (addr) =>
              addr.address_type_id === selectedAddressType ||
              addr.address_type_id?.toString() === selectedAddressType?.toString(),
          ) && (
            <div className="border-2 border-dashed border-section-border rounded-2xl p-8 text-center bg-section-surface">
              <div className="relative inline-block mb-3">
                <div className="absolute inset-0 bg-gold/10 rounded-full blur-xl opacity-30"></div>
                <MapPin className="relative w-12 h-12 text-section-muted mx-auto" />
              </div>
              <h3 className="text-lg font-bold text-section-text mb-2">No Address Found</h3>
              <p className="text-section-muted mb-5 max-w-xs mx-auto">
                Add an address for this type to continue with your order
              </p>
              <button
                onClick={() => {
                  const selectedType = addressTypes.find(
                    (type) => type.id === selectedAddressType || type.id.toString() === selectedAddressType?.toString(),
                  );
                  if (selectedType) onShowAddAddressModal();
                }}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-xl font-bold hover:opacity-90 hover:-translate-y-0.5 transition-all shadow-[0_4px_14px_rgba(201,169,110,0.2)]"
              >
                <Plus className="w-5 h-5" />
                Add Address for this Type
              </button>
            </div>
          )}

        <ConfirmModal
          isOpen={confirmDeleteOpen}
          title="Delete address"
          message="Are you sure you want to delete this address? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          variant="danger"
          onConfirm={() => {
            if (selectedAddress) onDeleteAddress(selectedAddress.id);
            setConfirmDeleteOpen(false);
          }}
          onCancel={() => setConfirmDeleteOpen(false)}
        />
      </div>
    </div>
  );
};

export default AddressManager;
